import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { useApi, githubAuthApiRef, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { TabProps, TenantConfig } from './types';
import { VariableEditor, EditorProvider } from '../TerraformVariablesEditor';
import {
  fetchModuleVariables,
  fetchModuleVersions,
  TerraformVariableSchema,
  FetchVariablesOptions,
  ModuleVersionInfo,
} from '../services/registryClient';
import { parseModuleSource } from '../services/moduleSourceParser';

const MODULE_SOURCE = 'app.terraform.io/Badal_devex/create-tenant/platform';

type EditorMode = 'gui' | 'text' | 'raw';

/**
 * Convert TenantConfig to the flat key-value object that maps to Terraform module variables.
 * This is the object the module expects as input.
 */
function configToModuleValues(config: TenantConfig): Record<string, unknown> {
  const values: Record<string, unknown> = {
    name: config.tenant_name,
    environments: Object.fromEntries(
      config.environments.map(env => [
        env.name,
        {
          service_account_roles: env.service_account_roles,
          enable_billing_user_role: env.enable_billing_user_role,
          additional_apis: config.additional_apis,
          labels: Object.fromEntries(
            config.labels.map(l => [l.key, l.value]),
          ),
          ...env.extra,
        },
      ]),
    ),
    topics: config.topics,
    budget: {
      amount: config.budget_amount,
      calendar_period: config.budget_calendar_period,
    },
    lifetime_budget: config.lifetime_budget,
    branching_model: config.branching_model === 'trunk_based'
      ? { trunk_based: { deployment_trigger: config.deployment_trigger } }
      : { environment_based: {} },
    teams: config.teams.length > 0
      ? Object.fromEntries(config.teams.map(t => [t.name, { permission: t.permission }]))
      : undefined,
    template: config.template_repo
      ? {
          repository: config.template_repo,
          include_all_branches: config.include_all_branches,
        }
      : undefined,
    tfc: {
      share_state: config.share_state,
    },
  };

  // Ownership
  if (config.owner_name) values.owner_name = config.owner_name;
  if (config.cost_center) values.cost_center = config.cost_center;
  if (config.owner_email) values.owner_email = config.owner_email;
  if (config.owner_team) values.owner_team = config.owner_team;

  // Purpose & metadata
  if (config.purpose) values.purpose = config.purpose;
  if (config.description) values.description = config.description;
  if (config.project_expiry) values.project_expiry = config.project_expiry;
  if (config.project_creation) values.project_creation = config.project_creation;

  return values;
}

export function TerraformTab({ config, onChange }: TabProps) {
  const githubAuth = useApi(githubAuthApiRef);
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const [variables, setVariables] = useState<TerraformVariableSchema[]>([]);
  const [versions, setVersions] = useState<ModuleVersionInfo[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>('latest');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<EditorMode>('gui');
  const [rawJson, setRawJson] = useState('');
  const [rawJsonError, setRawJsonError] = useState<string | null>(null);

  const sourceInfo = useMemo(() => parseModuleSource(MODULE_SOURCE), []);

  // The module values derived from the shared config
  const moduleValues = useMemo(() => configToModuleValues(config), [config]);

  // Fetch available versions
  useEffect(() => {
    const load = async () => {
      try {
        const token = await githubAuth.getAccessToken(['repo']);
        const result = await fetchModuleVersions(sourceInfo, {
          githubToken: token,
          fetchApi,
          backendUrl,
        });
        setVersions(result.versions);
      } catch {
        // Non-critical
      }
    };
    load();
  }, [githubAuth, fetchApi, sourceInfo, backendUrl]);

  // Fetch variable schemas for selected version
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await githubAuth.getAccessToken(['repo']);
        const options: FetchVariablesOptions = {
          githubToken: token,
          fetchApi,
          backendUrl,
          currentValues: moduleValues,
        };
        if (selectedVersion !== 'latest') {
          options.version = selectedVersion;
        }
        const result = await fetchModuleVariables(sourceInfo, options);
        setVariables(result.variables);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch module variables');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [githubAuth, fetchApi, sourceInfo, selectedVersion, backendUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync raw JSON when switching to raw mode or when config changes while in raw mode
  useEffect(() => {
    if (editorMode === 'raw') {
      setRawJson(JSON.stringify(moduleValues, null, 2));
      setRawJsonError(null);
    }
  }, [editorMode, moduleValues]);

  const handleVariableChange = useCallback(
    (name: string, value: unknown) => {
      // Map module variable changes back to TenantConfig fields
      switch (name) {
        case 'name':
          onChange({ ...config, tenant_name: value as string });
          break;
        case 'lifetime_budget':
          onChange({ ...config, lifetime_budget: value as number });
          break;
        default:
          // For complex fields, store as module_variables override
          // This handles fields not directly mapped to simple config keys
          break;
      }
    },
    [config, onChange],
  );

  const handleRawJsonChange = useCallback(
    (text: string) => {
      setRawJson(text);
      try {
        JSON.parse(text);
        setRawJsonError(null);
        // Raw JSON edits are read-only view of the config for now
        // Users should use other tabs to edit individual fields
      } catch {
        setRawJsonError('Invalid JSON');
      }
    },
    [],
  );

  const handleModeChange = useCallback(
    (_: unknown, newMode: EditorMode | null) => {
      if (newMode) setEditorMode(newMode);
    },
    [],
  );

  const requiredVars = useMemo(
    () => variables.filter(v => v.required && !v.sensitive),
    [variables],
  );
  const optionalVars = useMemo(
    () => variables.filter(v => !v.required && !v.sensitive),
    [variables],
  );
  const sensitiveVars = useMemo(
    () => variables.filter(v => v.sensitive),
    [variables],
  );

  if (loading) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <CircularProgress size={20} />
        <Typography>Loading module variables...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <EditorProvider>
      <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Typography variant="subtitle1">
              Module: <code>{MODULE_SOURCE}</code>
            </Typography>
            {versions.length > 0 && (
              <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                <InputLabel id="version-label">Version</InputLabel>
                <Select
                  labelId="version-label"
                  value={selectedVersion}
                  onChange={e => setSelectedVersion(e.target.value as string)}
                  label="Version"
                >
                  <MenuItem value="latest">Latest</MenuItem>
                  {versions.map(v => (
                    <MenuItem key={v.version} value={v.version}>
                      {v.version}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <ToggleButtonGroup
            value={editorMode}
            exclusive
            onChange={handleModeChange}
            size="small"
          >
            <ToggleButton value="gui">GUI</ToggleButton>
            <ToggleButton value="text">Text</ToggleButton>
            <ToggleButton value="raw">Raw JSON</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider />

        {/* Raw JSON mode — shows the full module values derived from config */}
        {editorMode === 'raw' && (
          <Box>
            {rawJsonError && <Alert severity="error" style={{ marginBottom: 8 }}>{rawJsonError}</Alert>}
            <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
              This JSON represents the module input values derived from your configuration. Edit individual fields using the other tabs.
            </Typography>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              minRows={15}
              maxRows={30}
              value={rawJson}
              onChange={e => handleRawJsonChange(e.target.value)}
              InputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            />
          </Box>
        )}

        {/* Text mode */}
        {editorMode === 'text' && (
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            {renderTextSection('Required Variables', requiredVars, moduleValues, handleVariableChange)}
            {renderTextSection('Optional Variables', optionalVars, moduleValues, handleVariableChange)}
            {renderTextSection('Sensitive Variables', sensitiveVars, moduleValues, handleVariableChange)}
          </Box>
        )}

        {/* GUI mode */}
        {editorMode === 'gui' && (
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            {renderGuiSection('Required Variables', requiredVars, moduleValues, handleVariableChange)}
            {renderGuiSection('Optional Variables', optionalVars, moduleValues, handleVariableChange)}
            {renderGuiSection('Sensitive Variables', sensitiveVars, moduleValues, handleVariableChange)}
          </Box>
        )}
      </Box>
    </EditorProvider>
  );
}

function renderGuiSection(
  title: string,
  vars: TerraformVariableSchema[],
  values: Record<string, unknown>,
  onChange: (name: string, value: unknown) => void,
) {
  if (vars.length === 0) return null;
  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>{title}</Typography>
      <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
        {vars.map(v => (
          <VariableEditor
            key={v.name}
            variable={v}
            value={values[v.name] ?? v.default ?? (v.type === 'string' ? '' : undefined)}
            onChange={val => onChange(v.name, val)}
          />
        ))}
      </Box>
    </Box>
  );
}

function renderTextSection(
  title: string,
  vars: TerraformVariableSchema[],
  values: Record<string, unknown>,
  onChange: (name: string, value: unknown) => void,
) {
  if (vars.length === 0) return null;
  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>{title}</Typography>
      <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
        {vars.map(v => {
          const currentVal = values[v.name] ?? v.default ?? '';
          const strVal = typeof currentVal === 'object'
            ? JSON.stringify(currentVal)
            : String(currentVal);
          return (
            <TextField
              key={v.name}
              label={v.name}
              helperText={v.description}
              variant="outlined"
              size="small"
              fullWidth
              value={strVal}
              onChange={e => {
                const raw = e.target.value;
                try {
                  onChange(v.name, JSON.parse(raw));
                } catch {
                  onChange(v.name, raw);
                }
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
